/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Observable, forkJoin, throwError } from 'rxjs';
import { catchError, map, mergeMap, shareReplay } from 'rxjs/operators';
import { ContextualRequestService } from './contextual-request.service';
import { PortfolioService } from './portfolio.service';
import { ConfigService } from 'src/app/service/config.service';
import { ParamsBuilder } from '../../utils/params-builder';

interface RelatedEntity {
  entity: any;
  sentiment: {
    volume_ratio: number;
  };
}

@Injectable({
  providedIn: 'root',
})
export class RelatedEntitiesService {
  private get project(): string {
    return this.config.appConfig.routing.apiproj;
  }

  private relatedCache = new Map<
    string,
    Observable<RelatedEntity[] | Record<string, RelatedEntity[]>>
  >();

  constructor(
    private contextualRequest: ContextualRequestService,
    private portfolioService: PortfolioService,
    private config: ConfigService,
  ) {}

  // Fetch related entities for one or more entity types.
  // When multiple types are passed (e.g. ['company', 'pne', 'country']),
  // a single API call is made and results are partitioned by entity.type.
  getRelatedEntities(
    iparams: any,
    entityTypes: string | string[],
    size: number,
  ): Observable<RelatedEntity[] | Record<string, RelatedEntity[]>> {
    const types = Array.isArray(entityTypes) ? entityTypes : [entityTypes];
    const key = this.cacheKey(iparams, types, size);
    const cached = this.relatedCache.get(key);
    if (cached) return cached;
    const result$ = this.doFetchRelatedEntities(
      iparams,
      entityTypes,
      types,
      size,
    ).pipe(
      shareReplay({ bufferSize: 1, refCount: false }),
      catchError((err) => {
        this.relatedCache.delete(key);
        return throwError(() => err);
      }),
    );
    this.relatedCache.set(key, result$);
    return result$;
  }

  clearCache(): void {
    this.relatedCache.clear();
  }

  private cacheKey(iparams: any, types: string[], size: number): string {
    return JSON.stringify({
      t: iparams?.type ?? null,
      i: iparams?.id ?? null,
      p: iparams?.portfolioId ?? null,
      tag: iparams?.tag ?? null,
      time: iparams?.time ?? null,
      u: iparams?.update ?? null,
      ev: iparams?.event_ids ?? null,
      types: [...types].sort().join(','),
      size,
    });
  }

  private doFetchRelatedEntities(
    iparams: any,
    entityTypes: string | string[],
    types: string[],
    size: number,
  ): Observable<RelatedEntity[] | Record<string, RelatedEntity[]>> {
    const resolvedParams = JSON.parse(JSON.stringify(iparams));
    const params = ParamsBuilder.build(
      resolvedParams,
      [
        'size',
        'entity_types',
        'event_ids',
        ...ParamsBuilder.PARAMS_FOR_ALL_REQUESTS,
      ],
      this.project,
    );

    params['top_entities_only'] = 'true';
    params['entity_types'] = types.join(',');
    params['size'] = size;

    return this.contextualRequest
      .request('related_entities', resolvedParams, params, 'v2')
      .pipe(
        mergeMap((res) => {
          if (!res?.related_entities) {
            return throwError(
              () => new Error('Error: wrong format of the response.'),
            );
          }
          const compoundKeys: string[] = res.related_entities.map(
            (el: any) => el.entity.compound_key,
          );
          if (compoundKeys.length === 0) {
            return Array.isArray(entityTypes)
              ? [types.reduce((acc, t) => ({ ...acc, [t]: [] }), {})]
              : [[]];
          }
          return this.enrichWithPortfolio(resolvedParams, compoundKeys).pipe(
            map((enriched) => {
              if (Array.isArray(entityTypes)) {
                return this.partitionByType(
                  enriched,
                  types,
                  res.related_entities,
                );
              }
              return enriched;
            }),
          );
        }),
      );
  }

  /**
   * Fetch event participants (companies involved in events).
   * Uses the related_events_participants endpoint.
   */
  getRelatedEventsParticipants(
    iparams: any,
    size: number,
  ): Observable<RelatedEntity[]> {
    const resolvedParams = JSON.parse(JSON.stringify(iparams));

    const params = ParamsBuilder.build(
      resolvedParams,
      ['size', 'entity_types', ...ParamsBuilder.PARAMS_FOR_ALL_REQUESTS],
      this.project,
    );

    params['entity_types'] = 'company';
    params['size'] = size;

    return this.contextualRequest
      .request('related_events_participants', resolvedParams, params, 'v2')
      .pipe(
        mergeMap((res) => {
          if (res?.related_events_participants) {
            const compoundKeys: string[] = res.related_events_participants.map(
              (el: any) => el.entity.compound_key,
            );
            return this.enrichWithPortfolio(resolvedParams, compoundKeys);
          }

          if (Array.isArray(res) && res[0]?.related_events_participants) {
            const requests = res.map((resEl: any) => {
              const keys: string[] = resEl.related_events_participants.map(
                (el: any) => el.entity.compound_key,
              );
              return this.enrichWithPortfolio(resolvedParams, keys);
            });
            return forkJoin(requests).pipe(map((results) => results.flat()));
          }

          return throwError(
            () => new Error('Error: wrong format of the response.'),
          );
        }),
      );
  }

  /**
   * Enrich compound keys with entity info + volume stats via PortfolioService.
   */
  private enrichWithPortfolio(
    iparams: any,
    compoundKeys: string[],
  ): Observable<RelatedEntity[]> {
    if (compoundKeys.length === 0) {
      return new Observable((sub) => {
        sub.next([]);
        sub.complete();
      });
    }
    return this.portfolioService.getPortfolio(iparams, compoundKeys).pipe(
      map((result) =>
        result.treemap.children.map((el: any) => ({
          entity: el.entity,
          sentiment: { volume_ratio: el.volume_ratio },
        })),
      ),
    );
  }

  /**
   * Partition enriched entities by their type, preserving the order
   * from the original API response.
   */
  private partitionByType(
    enriched: RelatedEntity[],
    types: string[],
    apiEntities: any[],
  ): Record<string, RelatedEntity[]> {
    const typeSet = new Set<string>();
    apiEntities.forEach((e: any) => typeSet.add(e.entity.type));

    const enrichedMap = new Map<string, RelatedEntity>();
    enriched.forEach((e) => enrichedMap.set(e.entity.compound_key, e));

    const result: Record<string, RelatedEntity[]> = {};
    types.forEach((t) => (result[t] = []));

    apiEntities.forEach((apiEntity: any) => {
      const key = apiEntity.entity.compound_key;
      const type = apiEntity.entity.type;
      const enrichedEntity = enrichedMap.get(key);
      if (enrichedEntity && result[type]) {
        result[type].push(enrichedEntity);
      }
    });

    return result;
  }
}
